import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import sharedStyles from '../Masters/Masters.module.css';
import Breadcrumb from '../../components/BreadCrumb/breadCrumb';
import Strings from '../../constants/stringConstant';
import Path from '../../constants/path';
import DropdownTextComp from '../../components/Dropdown/dropDownTextComp';
import TextFieldComp from '../../components/TextField/textFieldComp';
import TabSection from '../LoanApplicationsPage/Common/Tab/Tab';
import ButtonComp from '../../components/Button/buttonComp';
import makeApiCall from '../../api/makeApiCall';
import {
  fetchEmailTemplateMaster,
  fetchEmailTemplateName,
  fetchTemplateVariables,
  upsertEmailTemplate,
} from '../../api/request/masterFetchApi/masterFetchApi';

const EMAIL_TEMPLATE_NAMES = {
  1: 'Legal Review',
  2: 'Property Valuation',
  3: 'Fraud Control Unit',
};
const HTML_EDITOR = 'HTML Editor';
const PREVIEW = 'Preview';
const EDITOR_FONT = '13px/20px Menlo, Consolas, "Courier New", monospace';
const VARIABLE_CHIP_STYLE =
  'background:#FDF0E9;color:#F37021;border-radius:4px;padding:0 4px;font-family:monospace;';

const toPlaceholder = placeholder =>
  /[{$]/.test(placeholder) ? placeholder : `{{${placeholder}}}`;

const escapeHtml = text =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const highlightVariables = html =>
  html.replace(/\{\{\s*([^}<]+?)\s*\}\}/g, `<span style="${VARIABLE_CHIP_STYLE}">{{$1}}</span>`);

const buildPreviewHtml = html => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('[th\\:text], [th\\:utext]').forEach(el => {
    const expression = el.getAttribute('th:text') || el.getAttribute('th:utext');
    if (!el.textContent.trim()) {
      el.innerHTML = `<span style="${VARIABLE_CHIP_STYLE}">${escapeHtml(expression.replace(/^\$+/, '$'))}</span>`;
    }
  });
  doc.body.innerHTML = highlightVariables(doc.body.innerHTML);

  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
};

const outlinedBoxSx = {
  position: 'relative',
  border: '1px solid #E4E4E4',
  borderRadius: '8px',
  background: '#fff',
};

const outlinedLabelSx = {
  position: 'absolute',
  top: '-9px',
  left: '10px',
  padding: '0 4px',
  background: '#fff',
  color: '#7D778C',
  fontSize: '12px',
  lineHeight: '16px',
};

function EmailTemplate() {
  const paths = [
    { name: Strings.templateEngine.templateEngine, link: Path.template_engine },
    { name: Strings.templateEngine.emailTemplate, link: '' },
  ];
  const { roleBased } = useSelector(state => state.rbacValue);
  const isEdit = roleBased?.template_engine?.sections?.template_engine !== 'edit';

  const [templateListing, setTemplateListing] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [emailRecord, setEmailRecord] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [variableListing, setVariableListing] = useState([]);
  const [selectedVariable, setSelectedVariable] = useState('');
  const [activeTab, setActiveTab] = useState(HTML_EDITOR);
  const [isSaving, setIsSaving] = useState(false);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const gutterRef = useRef(null);
  const lastFocusedField = useRef('body');

  useEffect(() => {
    fetchTemplateNames();
  }, []);

  const fetchTemplateNames = async () => {
    try {
      const response = await makeApiCall(fetchEmailTemplateName());
      setTemplateListing(
        response?.data?.status === Strings.success ? response?.data?.data ?? [] : [],
      );
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const fetchEmailTemplate = async id => {
    try {
      const response = await makeApiCall(
        fetchEmailTemplateMaster({ searchTerm: '', pageNumber: 0, pageSize: 1, templateId: id }),
      );
      const record =
        response?.data?.status === Strings.success
          ? response?.data?.data?.content?.[0] ?? null
          : null;
      setEmailRecord(record);
      setSubject(record?.subject ?? '');
      setBody(record?.htmlTemplate ?? '');
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const fetchVariables = async id => {
    try {
      const response = await makeApiCall(
        fetchTemplateVariables({ templateId: id, isEmailTemplate: true }),
      );
      const data = response?.data?.status === Strings.success ? response?.data?.data ?? [] : [];
      setVariableListing(
        data.map(item => ({ variable: item.variable || '', placeholder: item.placeholder || '' })),
      );
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    }
  };

  const getTemplateLabel = template =>
    template?.templateName ||
    EMAIL_TEMPLATE_NAMES[template?.templateId] ||
    `Template ${template?.templateId}`;

  const handleTemplateChange = event => {
    const id = event.target.value;
    setTemplateId(id);
    setSelectedVariable('');
    setActiveTab(HTML_EDITOR);
    fetchEmailTemplate(id);
    fetchVariables(id);
  };

  const handleVariableInsert = event => {
    const placeholderValue = event.target.value;
    setSelectedVariable(placeholderValue);
    const isSubject = lastFocusedField.current === 'subject';
    const el = isSubject ? subjectRef.current : bodyRef.current;
    const value = isSubject ? subject : body;
    const setValue = isSubject ? setSubject : setBody;
    const token = toPlaceholder(placeholderValue);
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + token + value.slice(end));
    if (el) {
      el.focus();
      setTimeout(() => el.setSelectionRange(start + token.length, start + token.length), 0);
    }
  };

  const handleBodyKeyDown = event => {
    const el = bodyRef.current;
    const { selectionStart, selectionEnd } = el;
    if (event.key === 'Tab') {
      event.preventDefault();
      setBody(body.slice(0, selectionStart) + '  ' + body.slice(selectionEnd));
      setTimeout(() => el.setSelectionRange(selectionStart + 2, selectionStart + 2), 0);

      return;
    }
    if ((event.key !== 'Backspace' && event.key !== 'Delete') || selectionStart !== selectionEnd) return;
    const before = body.lastIndexOf('{{', selectionStart);
    const after = body.indexOf('}}', selectionStart);
    if (before !== -1 && after !== -1 && selectionStart > before && selectionStart <= after + 2) {
      setBody(body.slice(0, before) + body.slice(after + 2));
      setTimeout(() => el.setSelectionRange(before, before), 0);
      event.preventDefault();
    }
  };

  const handleApply = async () => {
    if (!subject?.trim() || !body?.trim()) {
      toast.error('Subject and body are required', { position: 'top-right' });

      return;
    }
    setIsSaving(true);
    try {
      const response = await makeApiCall(
        upsertEmailTemplate({
          id: templateId,
          vendorType: emailRecord?.vendorType,
          parallelWorkflowId: emailRecord?.parallelWorkflowId || 0,
          subject: subject ?? '',
          htmlTemplate: body ?? '',
          status: emailRecord?.status ?? true,
        }),
      );
      if (response?.data?.status === Strings.success) {
        toast.success('Template saved successfully', { position: 'top-right' });
        fetchEmailTemplate(templateId);
      } else {
        toast.error(response?.data?.error?.errorMessage || Strings.apiError, {
          position: 'top-right',
        });
      }
    } catch {
      toast.error(Strings.apiError, { position: 'top-right' });
    } finally {
      setIsSaving(false);
    }
  };

  const lineCount = Math.max(body.split('\n').length, 1);

  const editorContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <TextFieldComp
        label="Subject"
        name="subject"
        value={subject}
        onChange={event => setSubject(event.target.value)}
        onBlur={() => {
          lastFocusedField.current = 'subject';
        }}
        inputRef={subjectRef}
        maxLength={200}
      />
      <Box sx={outlinedBoxSx}>
        <Box component="span" sx={outlinedLabelSx}>Body</Box>
        <Box sx={{ display: 'flex', height: '420px', overflow: 'hidden', padding: '12px 0' }}>
          <Box
            ref={gutterRef}
            component="pre"
            sx={{
              margin: 0,
              padding: '0 10px 0 12px',
              font: EDITOR_FONT,
              color: '#9E9AA8',
              textAlign: 'right',
              userSelect: 'none',
              overflow: 'hidden',
              minWidth: '40px',
            }}>
            {Array.from({ length: lineCount }, (item, index) => index + 1).join('\n')}
          </Box>
          <textarea
            ref={bodyRef}
            name="body"
            value={body}
            spellCheck={false}
            onChange={event => setBody(event.target.value)}
            onKeyDown={handleBodyKeyDown}
            onScroll={event => {
              if (gutterRef.current) gutterRef.current.scrollTop = event.target.scrollTop;
            }}
            onBlur={() => {
              lastFocusedField.current = 'body';
            }}
            placeholder="Write email body HTML here..."
            style={{
              width: '100%',
              fontFamily: 'Manrope',
              fontSize: 16,
              padding: 8,
              borderRadius: 4,
              border: `2px solid ${'#f37021'}`,
              resize: 'vertical',
              minHeight: 300,
              whiteSpace: 'pre',
              tabSize: 2,
              outline: 'none',
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  const previewContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box sx={{ ...outlinedBoxSx, padding: '14px 12px' }}>
        <Box component="span" sx={outlinedLabelSx}>Subject</Box>
        <span
          className="text-medium-14px"
          dangerouslySetInnerHTML={{ __html: highlightVariables(escapeHtml(subject || '')) }}
        />
      </Box>
      <Box sx={{ ...outlinedBoxSx, padding: '8px' }}>
        <Box component="span" sx={outlinedLabelSx}>Body</Box>
        <iframe
          title="Email Template Preview"
          style={{ width: '100%', height: '420px', border: 'none', background: '#fff' }}
          srcDoc={buildPreviewHtml(body)}
        />
      </Box>
    </Box>
  );

  const tabs = [
    { tabName: HTML_EDITOR, component: editorContent },
    { tabName: PREVIEW, component: previewContent },
  ];

  return (
    <Box className={sharedStyles.main_container}>
      <Breadcrumb paths={paths} />
      <Box className={sharedStyles.secondary_container}>
        <Box className={'text-medium-16px light-purple-color'}>
          {Strings.templateEngine.emailTemplateDetail}
        </Box>
        <Grid container spacing={'16px'} sx={{ marginTop: 0 }}>
          <Grid item xs={12} md={6}>
            <DropdownTextComp
              label="Template Name"
              name="templateId"
              selectedValue={templateId}
              handleChange={handleTemplateChange}
              options={templateListing.map(template => ({
                value: template.templateId,
                label: getTemplateLabel(template),
              }))}
              required
            />
          </Grid>
        </Grid>
        {templateId !== '' && (
          <>
            <Box sx={{ marginTop: '16px' }}>
              <TabSection
                loanType="Display"
                tabValue={activeTab}
                handleChange={tabName => {
                  setActiveTab(tabName);
                  if (tabName !== HTML_EDITOR) setSelectedVariable('');
                }}
                tabs={tabs}
              />
            </Box>
            <Grid container spacing={'16px'} sx={{ marginTop: 0 }}>
              <Grid item xs={12} md={8}>
                <Box sx={{ ...outlinedBoxSx, padding: '16px' }}>
                  {tabs.find(tab => tab.tabName === activeTab)?.component}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    ...outlinedBoxSx,
                    padding: '16px',
                    height: '100%',
                    minHeight: '420px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <Box className={'text-medium-14px light-purple-color'}>
                    {Strings.templateEngine.addVariable}
                  </Box>
                  <DropdownTextComp
                    label="Variable"
                    name="variable"
                    selectedValue={selectedVariable}
                    handleChange={handleVariableInsert}
                    options={variableListing.map(item => ({
                      value: item.placeholder,
                      label: item.variable,
                    }))}
                    disabled={activeTab !== HTML_EDITOR}
                  />
                  <Box sx={{ marginTop: 'auto', paddingTop: '24px' }}>
                    <ButtonComp
                      className="text-bold-16px"
                      value="Apply"
                      gradient="linear-gradient(168deg, #F37021 -13.5%, #FDBA12 119.9%)"
                      height="48px"
                      width="100%"
                      padding="14px 24px"
                      textcolor="#fff"
                      borderRadius="8px"
                      disabled={isEdit || isSaving}
                      onClick={handleApply}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
}

export default EmailTemplate;
